import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { timelineMessages } from '../messages/timelineMessages';
import { AddEffectMenu } from './AddEffectMenu';
import type { TimelineZoom } from './timelineGeometry';
import styles from './TimelineBar.module.scss';

interface TimelineBarProps {
  zoom: TimelineZoom;
  onZoom: (zoom: TimelineZoom) => void;
  /** A section is selected: "Section" can fit it. */
  hasSection: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  /** Some cuts in view are too close to grab at this zoom. */
  cutsHidden: boolean;
  snapOn: boolean;
  onSnap: () => void;
}

/**
 * The bar of the timeline (design Timeline): the zoom (whole song, section, − and +), snap to the
 * beat, the tempo, "+ Add effect" (design directed-video h3) and "Recut the whole video".
 */
const TimelineBarComponent: React.FC<TimelineBarProps> = ({
  zoom,
  onZoom,
  hasSection,
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  cutsHidden,
  snapOn,
  onSnap,
}) => {
  const intl = useIntl();
  const { songMap, sectionIndex, openRecut, readOnly } = useMusicVideoEditor();
  const sectionLabel = songMap.sections.find((section) => section.index === sectionIndex)?.label;
  return (
    <div className={styles.bar}>
      <b>{intl.formatMessage(timelineMessages.title)}</b>
      <div className={styles.zoom} role="group" aria-label={intl.formatMessage(timelineMessages.zoomLabel)}>
        <button
          type="button"
          className={clsx(zoom === 'song' && styles.zoomOn)}
          aria-pressed={zoom === 'song'}
          onClick={() => onZoom('song')}
        >
          {intl.formatMessage(timelineMessages.zoomSong)}
        </button>
        <button
          type="button"
          className={clsx(zoom === 'section' && hasSection && styles.zoomOn)}
          aria-pressed={zoom === 'section'}
          disabled={!hasSection}
          onClick={() => onZoom('section')}
        >
          {sectionLabel ?? intl.formatMessage(timelineMessages.zoomSection)}
        </button>
      </div>
      <div className={styles.zoom}>
        <button
          type="button"
          className={styles.zoomStep}
          aria-label={intl.formatMessage(timelineMessages.zoomOut)}
          title={`${intl.formatMessage(timelineMessages.zoomOut)} (−)`}
          disabled={!canZoomOut}
          onClick={onZoomOut}
        >
          −
        </button>
        <button
          type="button"
          className={styles.zoomStep}
          aria-label={intl.formatMessage(timelineMessages.zoomIn)}
          title={`${intl.formatMessage(timelineMessages.zoomIn)} (+)`}
          disabled={!canZoomIn}
          onClick={onZoomIn}
        >
          +
        </button>
      </div>
      {cutsHidden && !readOnly && (
        <button type="button" className={clsx(styles.snap, styles.hint)} onClick={onZoomIn}>
          {intl.formatMessage(timelineMessages.zoomHint)}
        </button>
      )}
      <span className={styles.separator} />
      <button type="button" className={styles.snap} aria-pressed={snapOn} onClick={onSnap}>
        <i className={clsx(styles.toggle, snapOn && styles.toggleOn)} />
        {intl.formatMessage(timelineMessages.snap)}
      </button>
      <span className={clsx(styles.snap, styles.muted)}>
        {intl.formatMessage(timelineMessages.tempo, { bpm: Math.round(songMap.bpm) })}
      </span>
      <span className={styles.grow} />
      <span className={clsx(styles.snap, styles.muted)}>
        <span className={styles.mineDot} />
        {intl.formatMessage(timelineMessages.yourFootage)}
      </span>
      <AddEffectMenu />
      <button type="button" className={styles.recutAll} disabled={readOnly} onClick={() => openRecut('all')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
          <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
        </svg>
        {intl.formatMessage(timelineMessages.recutAll)}
      </button>
    </div>
  );
};

export const TimelineBar = TypedMemo(TimelineBarComponent);
